import { ClassicDataSubsystem } from 'helper/flux/automatic';

export interface ClassicCardWeek {
  label: string;
  semop: string;
  value: number | null;
}

export interface ClassicCard {
  label: string;
  weeks: ClassicCardWeek[];
  monthMWmed: number | null;
  mltMWmed: number | null;
  monthPercentMLT: number | null;
}

const MONTH_MWMED = /m[êe]s.*mwmed/i;
const MLT_MWMED = /^\s*mlt.*mwmed/i;
const MONTH_PERCENT = /m[êe]s.*%\s*mlt/i;
const WEEK = /sem\s*(\d+).*?(\d{6})/i;

// A GeneralTable trata valores falsy como N/A (`displayValue`). Os cards seguem a mesma
// regra para que card e tabela nunca mostrem coisas diferentes para a mesma célula.
const toValue = (raw: unknown): number | null => (
  typeof raw === 'number' && Number.isFinite(raw) && raw !== 0 ? raw : null
);

/**
 * Traduz um bloco do Clássico em cards. Devolve `null` quando o layout de colunas não é
 * reconhecível — nesse caso a tela deve cair para a tabela, nunca renderizar card com
 * número adivinhado.
 */
export default function buildClassicCards(
  block: ClassicDataSubsystem | undefined,
): ClassicCard[] | null {
  if (!block?.colLabels?.length || !block?.rowLabels?.length || !block?.values?.length) {
    return null;
  }

  const { colLabels, rowLabels, values } = block;

  let monthIndex = colLabels.findIndex((label) => MONTH_MWMED.test(label));
  let mltIndex = colLabels.findIndex((label) => MLT_MWMED.test(label));
  let percentIndex = colLabels.findIndex((label) => MONTH_PERCENT.test(label));

  // Fallback posicional: as três últimas colunas, nesta ordem.
  if (monthIndex < 0 || mltIndex < 0 || percentIndex < 0) {
    if (colLabels.length < 4) return null;
    monthIndex = colLabels.length - 3;
    mltIndex = colLabels.length - 2;
    percentIndex = colLabels.length - 1;
  }

  const tail = [monthIndex, mltIndex, percentIndex];
  const weekIndexes = colLabels
    .map((_, index) => index)
    .filter((index) => !tail.includes(index));

  if (!weekIndexes.length) return null;

  return rowLabels.map((label, rowIndex) => {
    const row = values[rowIndex] ?? [];
    return {
      label,
      weeks: weekIndexes.map((index) => {
        const match = WEEK.exec(colLabels[index]);
        return {
          label: match ? `Sem ${match[1]}` : colLabels[index],
          semop: match ? match[2] : '',
          value: toValue(row[index]),
        };
      }),
      monthMWmed: toValue(row[monthIndex]),
      mltMWmed: toValue(row[mltIndex]),
      monthPercentMLT: toValue(row[percentIndex]),
    };
  });
}
